import { useApp } from '@/app/AppContext'
import { CellValue, labelKey } from '@/lists/cellValue'
import type { ColumnDescriptor } from '@/lists/types'
import { RecordPicker } from './RecordPicker'
import { useEffectiveTaxRates } from './taxRateApi'
import type { CardSection, FieldError, FormValues } from './types'

/**
 * По какой колонке искать запись при выборе ссылки, и что показать рядом.
 *
 * Только для списков, у которых есть понятное название; ссылка на что-то иное правится
 * не в карточке (стадия — доменным переносом, склад — документом), и поле ввода над ней
 * предлагало бы вписать UUID.
 */
const PICKER_COLUMNS: Record<string, { search: string; extra?: string }> = {
  'sales.counterparty': { search: 'name', extra: 'inn' },
  'sales.product': { search: 'name', extra: 'sku' },
  'sales.deal': { search: 'number', extra: 'title' },
  'hr.employee': { search: 'last_name', extra: 'first_name' },
  'hr.department': { search: 'name' },
  'billing.contract': { search: 'number' },
  'billing.invoice': { search: 'number' },
}

/**
 * Колонки, значение которых выбирается из справочника, а не вводится, — ключ
 * `<сущность>.<колонка>`.
 *
 * Код ставки налога у товара (V57): поле ввода позволило бы вписать «20» или код, которого
 * нет в справочнике, и отказ пришёл бы от базы при сохранении.
 */
const CHOICE_COLUMNS: Record<string, 'tax-rate'> = {
  'sales.product.tax_rate_code': 'tax-rate',
}

/**
 * Форма карточки: секции полей в режиме чтения или правки (тикет #66, ТЗ §5.1).
 *
 * Тип поля определяет виджет, и берётся он из описания колонок сервера, а не из имени:
 * привязка к имени (`*_at` — дата, `is_*` — флажок) разъезжается при первой правке
 * каталога и делает это молча.
 */

interface CardFormProps {
  /** Ключ каталога карточки — для перевода кодов значений. */
  entity?: string
  /** Секции, из которых уже вычтены поля без полевого права. */
  sections: CardSection[]
  columns: Map<string, ColumnDescriptor>
  values: FormValues
  errors: FieldError[]
  editing: boolean
  /** Колонки, которые в режиме правки показываются, но не правятся (#100). */
  readOnlyColumns?: readonly string[]
  onChange: (field: string, value: unknown) => void
}

export function CardForm({
  entity,
  sections,
  columns,
  values,
  errors,
  editing,
  readOnlyColumns = [],
  onChange,
}: CardFormProps) {
  const { t } = useApp()
  const errorByField = new Map(errors.map((error) => [error.field, error.message]))

  // При просмотре группа, где не заполнено ни одно поле, не показывается: блок «Физическое
  // лицо» из одних прочерков на карточке юрлица — это шум, который отодвигает вниз то, что
  // заполнено. При правке группа остаётся: её как раз и заполняют.
  const shown = editing
    ? sections
    : sections.filter((section) =>
        section.columns.some((name) => !isEmpty(values[name])),
      )

  return (
    <div className="card-form">
      {shown.map((section) => (
        <fieldset key={section.labelKey} className="card-section">
          <legend>{t(section.labelKey)}</legend>
          <dl>
            {section.columns.map((name) => {
              const descriptor = columns.get(name)
              if (!descriptor) return null
              const error = errorByField.get(name)
              const inputId = `field-${name}`

              return (
                <div key={name} className="card-field">
                  <dt>
                    <label htmlFor={inputId}>{t(`column.${name}`)}</label>
                  </dt>
                  <dd>
                    {editing && !readOnlyColumns.includes(name) && editable(descriptor) ? (
                      descriptor.reference ? (
                        <RecordPicker
                          id={inputId}
                          entity={descriptor.reference.entity as string}
                          searchColumn={PICKER_COLUMNS[descriptor.reference.entity as string]!.search}
                          extraColumn={PICKER_COLUMNS[descriptor.reference.entity as string]!.extra}
                          value={
                            typeof values[name] === 'string'
                              ? {
                                  id: values[name] as string,
                                  label: String(values[labelKey(name)] ?? values[name]),
                                }
                              : null
                          }
                          onChange={(picked) => {
                            onChange(name, picked?.id ?? null)
                            onChange(labelKey(name), picked?.label ?? null)
                          }}
                        />
                      ) : entity && CHOICE_COLUMNS[`${entity}.${name}`] === 'tax-rate' ? (
                        <TaxRateSelect
                          id={inputId}
                          value={typeof values[name] === 'string' ? (values[name] as string) : ''}
                          invalid={Boolean(error)}
                          onChange={(value) => onChange(name, value)}
                        />
                      ) : (
                        <FieldEditor
                          id={inputId}
                          descriptor={descriptor}
                          value={values[name]}
                          invalid={Boolean(error)}
                          onChange={(value) => onChange(name, value)}
                        />
                      )
                    ) : isEmpty(values[name]) ? (
                      // Прочерк вместо слова «null»: оно читается как значение, и его
                      // начинают искать в справочниках.
                      <span>—</span>
                    ) : (
                      <span>
                        <CellValue
                          values={values}
                          column={name}
                          descriptor={descriptor}
                          entity={entity}
                        />
                      </span>
                    )}
                    {error && (
                      <span className="field-error" role="alert">
                        {t(error)}
                      </span>
                    )}
                  </dd>
                </div>
              )
            })}
          </dl>
        </fieldset>
      ))}
    </div>
  )
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

/**
 * Правится ли поле в карточке.
 *
 * Ссылка правится выбором записи — и только если у цели есть свой список с понятным
 * названием. Ссылка без такого списка (стадия, склад, организация) показывается названием:
 * поле ввода над ней предлагало бы вписать UUID, а меняются такие вещи доменным действием.
 */
function editable(descriptor: ColumnDescriptor): boolean {
  if (!descriptor.reference) return true
  const target = descriptor.reference.entity
  return Boolean(target && PICKER_COLUMNS[target])
}

/**
 * Выбор ставки налога из справочника: действующие сегодня, без расчётных.
 *
 * Расчётные ставки (22/122) — для авансов в чеке, а не для товара. Текущее значение, которого
 * среди действующих нет (ставка закрыта), остаётся в списке кодом: молча подменить его
 * первой попавшейся значило бы переставить товару налог тем, что форму открыли.
 */
function TaxRateSelect({ id, value, invalid, onChange }: {
  id: string
  value: string
  invalid: boolean
  onChange: (value: string) => void
}) {
  const { t } = useApp()
  const rates = useEffectiveTaxRates()
  const options = (rates.data ?? []).filter((rate) => !rate.estimated)
  const known = options.some((rate) => rate.code === value)
  return (
    <select id={id} className="input" aria-invalid={invalid || undefined} value={value}
            disabled={rates.isLoading} onChange={(event) => onChange(event.target.value)}>
      {!value && <option value="">{t('card.taxRate.pick')}</option>}
      {value && !known && <option value={value}>{value}</option>}
      {options.map((rate) => (
        <option key={rate.id} value={rate.code}>{`${rate.name} — ${rate.code}`}</option>
      ))}
    </select>
  )
}

interface FieldEditorProps {
  id: string
  descriptor: ColumnDescriptor
  value: unknown
  invalid: boolean
  onChange: (value: unknown) => void
}

/**
 * Виджет одного поля по типу колонки.
 *
 * Дата правится `<input type="date">`, а не текстом: свободный ввод даты — это спор о
 * форматах, в котором «01.02.2026» и «02/01/2026» выглядят одинаково правдоподобно и
 * означают разное.
 */
function FieldEditor({ id, descriptor, value, invalid, onChange }: FieldEditorProps) {
  const common = {
    id,
    'aria-invalid': invalid || undefined,
  }

  switch (descriptor.type) {
    case 'boolean':
      return (
        <input
          {...common}
          type="checkbox"
          checked={value === true || value === 'true'}
          onChange={(event) => onChange(event.target.checked)}
        />
      )

    case 'number':
      return (
        <input
          {...common}
          type="number"
          // `step="any"` — иначе браузер объявляет невалидным дробное значение в поле
          // вроде ставки НДС или веса, и отказ приходит без единого сообщения.
          step="any"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      )

    case 'date':
      return (
        <input
          {...common}
          type="date"
          value={typeof value === 'string' ? value.slice(0, 10) : ''}
          onChange={(event) => onChange(event.target.value)}
        />
      )

    case 'timestamp':
      // Момент времени правится не здесь: `created_at` и `closed_at` ставит система, и
      // поле ввода над ними обещало бы правку, которую сервер всё равно не примет.
      return <span>{value === null || value === undefined ? '—' : String(value)}</span>

    case 'uuid':
    case 'text':
    default:
      return (
        <input
          {...common}
          type="text"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      )
  }
}
